import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import {
	type BalanceRowItem,
	createBalancesColumns,
} from "./balances-columns.tsx";
import {
	BALANCES_GRID_CLASS,
	HIDDEN_BALANCE_COINS,
} from "./constants/positions.constants.ts";
import { VirtualizedGridTable } from "./virtualized-grid-table.tsx";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

type BalancesTableProps = {
	balances: BalanceRowItem[];
	hideSmall: boolean;
	isError?: boolean;
	onRetry?: () => void;
};

export function BalancesTable({
	balances,
	hideSmall,
	isError,
	onRetry,
}: BalancesTableProps) {
	const { t } = useTranslate();

	const filtered = useMemo(
		() =>
			balances
				.filter((b) => !HIDDEN_BALANCE_COINS.has(b.coin))
				.filter((b) => {
					if (!hideSmall) return true;
					const usdValue =
						b.coin === "USDC" ? Number(b.total || 0) : Number(b.entryNtl || 0);
					return usdValue >= 1;
				}),
		[balances, hideSmall],
	);

	const columns = useMemo(() => createBalancesColumns(t), [t]);

	const table = useReactTable({
		data: filtered,
		columns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	return (
		<VirtualizedGridTable
			table={table}
			gridClass={BALANCES_GRID_CLASS}
			estimateSize={36}
			emptyMessage={t("positions.no-balances")}
			isError={isError}
			onRetry={onRetry}
		/>
	);
}
